import type { FeasibilityNeighbour, FeasibilityResponse, LocationChoice } from '@ergo/shared';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { INLINE_DEBOUNCE_MS } from '../hooks';
import { isValidDate, isValidTime, timeOf } from '../time';

export interface FeasibilityQuery {
  locationId: LocationChoice | null;
  date: string;
  time: string;
  durationMin: number;
  /** The séance being edited, which must not warn about itself. */
  excludeId?: number;
}

/**
 * Warnings about the slot being edited: an overlap, or a bike hop that does not
 * fit in the gap. Purely informative — DESIGN.md is explicit that she decides.
 * Any error is swallowed: a feasibility check must never get in the way of a save.
 */
export function Feasibility({ query }: { query: FeasibilityQuery }) {
  const [result, setResult] = useState<FeasibilityResponse | null>(null);
  const { locationId, date, time, durationMin, excludeId } = query;
  const ready = locationId !== null && isValidDate(date) && isValidTime(time);

  useEffect(() => {
    if (!ready || locationId === null) {
      setResult(null);
      return;
    }

    let active = true;
    const timer = setTimeout(() => {
      api.maps
        .feasibility({
          locationId,
          startsAt: `${date}T${time}`,
          durationMin,
          ...(excludeId === undefined ? {} : { excludeId }),
        })
        .then((response) => {
          if (active) setResult(response);
        })
        .catch(() => {
          if (active) setResult(null);
        });
    }, INLINE_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [ready, locationId, date, time, durationMin, excludeId]);

  if (!result) return null;

  const messages = [
    ...neighbourMessages(result.previous, 'previous'),
    ...neighbourMessages(result.next, 'next'),
  ];
  if (messages.length === 0) return null;

  return (
    <div role="status">
      {messages.map((message) => (
        <p className="warning" key={message}>
          {message}
        </p>
      ))}
    </div>
  );
}

function neighbourMessages(
  neighbour: FeasibilityNeighbour | null,
  side: 'previous' | 'next',
): string[] {
  if (!neighbour) return [];

  if (neighbour.overlaps) {
    return [`Chevauche la séance de ${neighbour.patientName} à ${timeOf(neighbour.startsAt)}.`];
  }

  if (neighbour.tight && neighbour.bikeMinutes !== null) {
    const where = side === 'previous' ? 'depuis la séance précédente' : 'vers la séance suivante';
    return [
      `${neighbour.bikeMinutes} min à vélo ${where} (${neighbour.patientName}) — l’écart est de ${neighbour.gapMin} min.`,
    ];
  }

  return [];
}
